/**
 * UploadProgress Component
 *
 * Displays real-time upload progress from Server-Sent Events (SSE).
 * Shows overall job progress and individual file progress.
 *
 * @module components/UploadProgress
 */

import React from 'react';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  FileText,
  Wifi,
  WifiOff,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { UploadProgressProps } from '@/features/upload/types/upload.types';
import {
  UPLOAD_JOB_STATUS_LABELS,
  UPLOAD_JOB_STATUS_COLORS,
  UPLOAD_FILE_STATUS_LABELS,
  UPLOAD_FILE_STATUS_COLORS,
  formatFileSize,
} from '@/features/upload/types/upload.types';

/**
 * Get status icon
 */
const getStatusIcon = (status: string) => {
  switch (status) {
    case 'uploading':
    case 'validating':
    case 'pending':
      return <Loader2 className="h-5 w-5 animate-spin text-info" />;
    case 'completed':
      return <CheckCircle2 className="h-5 w-5 text-success" />;
    case 'failed':
      return <XCircle className="h-5 w-5 text-destructive" />;
    case 'cancelled':
      return <AlertCircle className="h-5 w-5 text-muted-foreground" />;
    default:
      return <FileText className="h-5 w-5 text-muted-foreground" />;
  }
};

/**
 * Type guard to check if the object is an UploadJob (has dataset_name)
 */
const isUploadJob = (obj: any): obj is { dataset_name: string; created_at: string; completed_at?: string; total_size?: number; uploaded_size?: number } => {
  return obj && 'dataset_name' in obj;
};

/**
 * UploadProgress Component
 *
 * Displays real-time upload progress with SSE connection indicator.
 *
 * @example
 * ```tsx
 * <UploadProgress
 *   uploadJob={jobData}
 *   files={fileList}
 *   onCancel={() => cancelUpload()}
 *   onComplete={() => viewDataset()}
 *   isConnected={true}
 * />
 * ```
 */
export const UploadProgress: React.FC<UploadProgressProps> = ({
  uploadJob,
  files = [],
  onCancel,
  onComplete,
  isConnected = false,
  className,
}) => {
  if (!uploadJob) {
    return null;
  }

  const isCompleted = uploadJob.status === 'completed';
  const isFailed = uploadJob.status === 'failed';
  const isCancelled = uploadJob.status === 'cancelled';
  const isActive = uploadJob.status === 'uploading' || uploadJob.status === 'validating';

  return (
    <Card className={cn('w-full', className)}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              {getStatusIcon(uploadJob.status)}
              <CardTitle>Upload Progress</CardTitle>
            </div>
            {isUploadJob(uploadJob) && (
              <CardDescription>
                Dataset: <span className="font-medium">{uploadJob.dataset_name}</span>
              </CardDescription>
            )}
          </div>

          {/* Connection Indicator */}
          <div className="flex items-center gap-2">
            {isConnected ? (
              <>
                <Wifi className="h-4 w-4 text-success" />
                <span className="text-xs text-success">Live</span>
              </>
            ) : (
              <>
                <WifiOff className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Disconnected</span>
              </>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Overall Progress */}
        <div className="space-y-3">
          {/* Status Badge */}
          <div className="flex items-center justify-between">
            <Badge
              variant="secondary"
              className={cn(
                'text-sm',
                UPLOAD_JOB_STATUS_COLORS[uploadJob.status]
              )}
            >
              {UPLOAD_JOB_STATUS_LABELS[uploadJob.status]}
            </Badge>

            {/* Progress Percentage */}
            <span className="text-2xl font-bold">
              {uploadJob.progress_percentage}%
            </span>
          </div>

          {/* Progress Bar */}
          <Progress value={uploadJob.progress_percentage} className="h-2" />

          {/* File Counts */}
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="space-y-1">
              <p className="text-2xl font-bold">{uploadJob.total_files}</p>
              <p className="text-xs text-muted-foreground">Total Files</p>
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-success">
                {uploadJob.completed_files}
              </p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-bold text-destructive">
                {uploadJob.failed_files}
              </p>
              <p className="text-xs text-muted-foreground">Failed</p>
            </div>
          </div>

          {/* Size Info */}
          {isUploadJob(uploadJob) && uploadJob.total_size && uploadJob.uploaded_size !== undefined && (
            <div className="text-sm text-muted-foreground text-center">
              {formatFileSize(uploadJob.uploaded_size)} / {formatFileSize(uploadJob.total_size)}
            </div>
          )}
        </div>

        {/* Error Message */}
        {uploadJob.error_message && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-destructive">Error</p>
                <p className="text-xs text-destructive/90 mt-1">
                  {uploadJob.error_message}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Individual Files */}
        {files.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Files ({files.length})</h4>
            <ScrollArea className="h-[200px] rounded-md border">
              <div className="p-3 space-y-2">
                {files.map((file) => (
                  <div
                    key={file.id}
                    className={cn(
                      'flex items-center gap-3 rounded-lg border p-2 transition-colors',
                      file.status === 'failed' && 'border-destructive/50 bg-destructive/5'
                    )}
                  >
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      {file.status === 'uploading' || file.status === 'validating' ? (
                        <Loader2 className="h-4 w-4 animate-spin text-info" />
                      ) : file.status === 'completed' ? (
                        <CheckCircle2 className="h-4 w-4 text-success" />
                      ) : file.status === 'failed' ? (
                        <XCircle className="h-4 w-4 text-destructive" />
                      ) : (
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>

                    {/* File Info */}
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-medium truncate" title={file.filename}>
                          {file.filename}
                        </p>
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-xs flex-shrink-0',
                            UPLOAD_FILE_STATUS_COLORS[file.status]
                          )}
                        >
                          {UPLOAD_FILE_STATUS_LABELS[file.status]}
                        </Badge>
                      </div>

                      {/* Progress Bar for Active Files */}
                      {(file.status === 'uploading' || file.status === 'validating') && (
                        <div className="space-y-1">
                          <Progress value={file.progress_percentage} className="h-1" />
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>{formatFileSize(file.file_size)}</span>
                            <span>{file.progress_percentage}%</span>
                          </div>
                        </div>
                      )}

                      {/* Completed File Info */}
                      {file.status === 'completed' && file.row_count !== undefined && (
                        <p className="text-xs text-muted-foreground">
                          {file.row_count.toLocaleString()} rows •{' '}
                          {formatFileSize(file.file_size)}
                        </p>
                      )}

                      {/* Error Message */}
                      {file.status === 'failed' && file.error_message && (
                        <p className="text-xs text-destructive">{file.error_message}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between gap-2">
          {/* Cancel Button */}
          {isActive && onCancel && (
            <Button variant="outline" onClick={onCancel} className="flex-1">
              <XCircle className="h-4 w-4 mr-2" />
              Cancel Upload
            </Button>
          )}

          {/* Complete Button */}
          {(isCompleted || isFailed || isCancelled) && onComplete && (
            <Button onClick={onComplete} className="flex-1">
              {isCompleted ? (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  View Dataset
                </>
              ) : (
                <>
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Continue
                </>
              )}
            </Button>
          )}
        </div>

        {/* Timestamps */}
        {isUploadJob(uploadJob) && (
          <div className="text-xs text-muted-foreground space-y-1">
            <div className="flex justify-between">
              <span>Started:</span>
              <span>{new Date(uploadJob.created_at).toLocaleString()}</span>
            </div>
            {uploadJob.completed_at && (
              <div className="flex justify-between">
                <span>Completed:</span>
                <span>{new Date(uploadJob.completed_at).toLocaleString()}</span>
              </div>
            )}
            {uploadJob.completed_at && (
              <div className="flex justify-between font-medium">
                <span>Duration:</span>
                <span>
                  {Math.round(
                    (new Date(uploadJob.completed_at).getTime() -
                      new Date(uploadJob.created_at).getTime()) /
                      1000
                  )}{' '}
                  seconds
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
